import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  Button,
  Stack,
  Collapse,
  Divider,
  IconButton
} from '@mui/material';
import {
  SmartToy as AiIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { GameCardSkeleton } from './SkeletonLoaders';
import { sportKeyToShort } from '../utils/sportsMapping';
import { SportIcon } from '../utils/sportIcons';

// Helper function to format pick types for display
const formatPickType = (pickType) => {
  const pickTypeMap = {
    'spreads': 'Spread',
    'h2h': 'Moneyline',
    'totals': 'Total'
    // Period markets temporarily hidden for development
    // 'h2h_h1': 'Moneyline (1H)',
    // 'spreads_h1': 'Spread (1H)',
    // 'totals_h1': 'Total (1H)',
    // 'h2h_h2': 'Moneyline (2H)',
    // 'spreads_h2': 'Spread (2H)',
    // 'totals_h2': 'Total (2H)'
  };

  return pickTypeMap[pickType] || pickType || 'N/A';
};

// Helper function to format odds display
const formatOdds = (odds) => {
  if (typeof odds !== 'number') return 'N/A';

  if (odds > 0) {
    return `+${odds}`;
  } else if (odds < 0) {
    return `${odds}`;
  } else {
    return 'EVEN';
  }
};

// Helper function to get confidence chip color
const getConfidenceColor = (confidence) => {
  switch (confidence?.toLowerCase()) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to format sport display
const formatSport = (sport) => {
  return sportKeyToShort(sport) || '';
};

// Helper function to get sportsbook homepage URLs
const getSportsbookHomepage = (bookmakerTitle) => {
  const homepages = {
    'DraftKings': 'https://sportsbook.draftkings.com',
    'FanDuel': 'https://sportsbook.fanduel.com',
    'BetMGM': 'https://sports.betmgm.com',
    'Caesars': 'https://www.caesars.com/sportsbook',
    'PointsBet': 'https://pointsbet.com',
    'BetRivers': 'https://ny.betrivers.com/?page=sportsbook',
    'Unibet': 'https://www.unibet.com/betting',
    'Barstool': 'https://www.barstoolsportsbook.com',
    'TwinSpires': 'https://www.twinspires.com/sportsbook',
    'William Hill': 'https://www.williamhill.com/us',
    'BetUS': 'https://www.betus.com.pa/sportsbook',
    'BetAnySports': 'https://www.betanysports.com',
    'Bovada': 'https://www.bovada.lv/sports',
    'MyBookie': 'https://mybookie.ag/sportsbook',
    'XBet': 'https://www.xbet.ag/sportsbook',
    'LowVig': 'https://www.lowvig.ag/sports',
    'BetOnline': 'https://www.betonline.ag/sportsbook',
    'Fanatics': 'https://www.fanaticssportsbook.com'
  };

  return homepages[bookmakerTitle] || `https://www.google.com/search?q=${encodeURIComponent(bookmakerTitle + ' sportsbook')}`;
};



// Skeleton loader for FeaturedPicks
const FeaturedPicksSkeleton = ({ count = 10 }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        pb: 1,
        '&::-webkit-scrollbar': {
          height: 4
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.1)',
          borderRadius: 2
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'primary.main',
          borderRadius: 2,
          '&:hover': {
            background: 'primary.dark'
          }
        }
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            minWidth: { xs: 240, sm: 280 },
            maxWidth: { xs: 240, sm: 280 },
            flexShrink: 0
          }}
        >
          <GameCardSkeleton />
        </Box>
      ))}
    </Box>
  );
};

export const FeaturedPicks = ({ picks = [], loading = false, title }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedCards, setExpandedCards] = useState({});
  const { isPremium, loading: subscriptionLoading } = useSubscription();

  // Number of free preview picks for non-premium users
  const FREE_PREVIEW_COUNT = 0;

  const handlePickClick = (pick, index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleHandicapperClick = (pick, e) => {
    e.stopPropagation();
    if (pick?.handicapperSlug) {
      navigate(`/handicappers/${pick.handicapperSlug}`);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Optional Title */}
      {title && (
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {title}
        </Typography>
      )}

      {/* Loading State */}
      {loading && (
        <FeaturedPicksSkeleton count={10} />
      )}

      {/* Empty State */}
      {!loading && picks.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No Featured Picks Found
          </Typography>
        </Box>
      )}

      {/* Horizontal Scrolling Picks Feed */}
      {!loading && picks.length > 0 && (
        <Box
          sx={{
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              pb: 1,
              '&::-webkit-scrollbar': {
                height: 4
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: 2
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'primary.main',
                borderRadius: 2,
                '&:hover': {
                  background: 'primary.dark'
                }
              }
            }}
          >
            {picks.map((pick, index) => {
              const isLocked = !isPremium && index >= FREE_PREVIEW_COUNT;
              
              return (
                <Card
                  key={pick?.id || index}
                  elevation={1}
                  onClick={() => !isLocked && handlePickClick(pick, index)}
                sx={{
                  minWidth: { xs: 240, sm: 280 },
                  maxWidth: expandedCards[index] ? { xs: '90vw', sm: '500px' } : { xs: 240, sm: 280 },
                  minHeight: 150,
                  flexShrink: 0,
                  cursor: isLocked ? 'default' : 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    elevation: isLocked ? 1 : 4,
                    transform: isLocked ? 'none' : 'translateY(-2px)'
                  },
                  position: expandedCards[index] || isLocked ? 'relative' : 'static',
                  zIndex: expandedCards[index] ? 10 : 1,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ 
                  p: 1.5, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  filter: isLocked ? 'blur(4px)' : 'none',
                  pointerEvents: isLocked ? 'none' : 'auto'
                }}>
                  {/* Handicapper Name Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {pick?.aiGenerated && (
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: pick?.personalityColor || 'primary.main',
                            color: 'white'
                          }}
                        >
                          <AiIcon sx={{ fontSize: '14px' }} />
                        </Avatar>
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: pick?.personalityColor || (pick?.aiGenerated ? 'primary.main' : 'primary.main'),
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => handleHandicapperClick(pick, e)}
                      >
                        {pick?.handicapperName || 'Unknown Handicapper'}
                        {pick?.aiGenerated && (
                          <Chip
                            label={pick?.personalityName || "AI"}
                            size="small"
                            sx={{
                              ml: 1,
                              fontSize: '0.6rem',
                              height: 16,
                              fontWeight: 600,
                              bgcolor: pick?.personalityColor || 'primary.main',
                              color: 'white'
                            }}
                          />
                        )}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePickClick(pick, index);
                      }}
                    >
                      {expandedCards[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Sport Badge */}
                  {pick?.sport && (
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        icon={<SportIcon sportKey={pick.sport} fontSize="small" />}
                        label={formatSport(pick.sport)}
                        size="small"
                        color="primary"
                        variant="contained"
                        sx={{
                          fontSize: '0.65rem',
                          height: 18,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  )}

                  {/* Team Matchup */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: 'text.primary',
                      fontSize: '0.85rem'
                    }}
                  >
                    {pick?.awayTeam && pick?.homeTeam
                      ? `${pick.awayTeam} @ ${pick.homeTeam}`
                      : 'Teams TBD'
                    }
                  </Typography>

                  {/* Pick Details Section */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1
                    }}
                  >
                    {/* Pick Details */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>{formatPickType(pick?.pickType)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        Pick: {pick?.pickedTeam || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Odds (+ de-vigged true chance when the pick carries one) */}
                    {pick?.odds && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: 'text.primary'
                        }}
                      >
                        Odds: {formatOdds(pick.odds)}
                        {typeof pick?.fairProb === 'number' && (
                          <> · True chance {(pick.fairProb * 100).toFixed(0)}%</>
                        )}
                      </Typography>
                    )}

                    {/* Spacer to push buttons and confidence to bottom */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Bookmaker Button */}
                    {pick?.bookmaker && (
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(getSportsbookHomepage(pick.bookmaker), '_blank', 'noopener,noreferrer');
                          }}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            '&:hover': {
                              bgcolor: 'primary.dark'
                            },
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Bet on {pick.bookmaker}
                        </Button>
                      </Box>
                    )}

                    {/* Confidence Level */}
                    <Box sx={{ mt: 'auto' }}>
                      <Chip
                        label={`${(pick?.confidence || 'medium').charAt(0).toUpperCase()}${(pick?.confidence || 'medium').slice(1)} Confidence`}
                        size="small"
                        color={getConfidenceColor(pick?.confidence)}
                        variant="outlined"
                        sx={{
                          fontSize: '0.75rem',
                          height: 24
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Expanded Content */}
                  <Collapse in={expandedCards[index]} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mt: 2 }}>
                      {/* Analysis */}
                      {pick?.analysis && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                            Analysis
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                            {pick.analysis}
                          </Typography>
                        </Box>
                      )}

                      {/* Additional Details */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          Pick Details
                        </Typography>
                        <Stack spacing={1}>
                          {pick?.gameDate && (
                            <Typography variant="body2">
                              <strong>Game Date:</strong> {new Date(pick.gameDate).toLocaleDateString()}
                            </Typography>
                          )}
                          {pick?.gameCommenceTime && (
                            <Typography variant="body2">
                              <strong>Game Commence Time:</strong> {new Date(pick.gameCommenceTime).toLocaleString()}
                            </Typography>
                          )}
                          {pick?.updatedAt && (
                            <Typography variant="body2">
                              <strong>Updated At:</strong> {new Date(pick.updatedAt).toLocaleString()}
                            </Typography>
                          )}
                          {pick?.pickReason && (
                            <Typography variant="body2">
                              <strong>Reason:</strong> {pick.pickReason}
                            </Typography>
                          )}
                          {pick?.keyFactors && pick.keyFactors.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Key Factors:
                              </Typography>
                              <Box sx={{ pl: 1 }}>
                                {pick.keyFactors.map((factor, idx) => (
                                  <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                    • {factor}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {pick?.value && (
                            <Typography variant="body2">
                              <strong>Value Rating:</strong> {pick.value}/10
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      {/* Additional Info */}
                      {(pick?.tags || pick?.lastUpdated || pick?.status) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                            Additional Info
                          </Typography>
                          <Stack spacing={1}>
                            {pick?.tags && pick.tags.length > 0 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Tags:</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {pick.tags.map((tag, idx) => (
                                    <Chip key={idx} label={tag} size="small" variant="outlined" />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            {pick?.lastUpdated && (
                              <Typography variant="body2">
                                <strong>Last Updated:</strong> {new Date(pick.lastUpdated).toLocaleString()}
                              </Typography>
                            )}
                            {pick?.status && (
                              <Typography variant="body2">
                                <strong>Status:</strong> {pick.status}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
                {isLocked && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/subscription');
                      }}
                      sx={{
                        pointerEvents: 'auto',
                        bgcolor: '#e8721f',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        px: 1.75,
                        py: 0.75,
                        borderRadius: 999,
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
                        '&:hover': { bgcolor: '#d1621a' },
                      }}
                    >
                      Subscribe to unlock
                    </Button>
                  </Box>
                )}
              </Card>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FeaturedPicks;